import { useState } from "react";
import { Plus, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDraft } from "@/components/itsm-config/draft-context";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { JiraFieldImport } from "@/components/itsm-config/provider-field-import";
import { useDesks } from "@/hooks/use-desk-migration";
import { useHarmonyFieldLabels } from "@/hooks/use-harmony-field-labels";
import { useSourceCatalogItem } from "@/hooks/use-intents";
import { useJiraSiteFields } from "@/hooks/use-itsm-metadata";
import {
  decodeField,
  encodeField,
  fieldIdentity,
} from "@/lib/itsm-config/field-mapping";
import {
  JIRA_BUILTIN_FIELDS,
  JIRA_WRITABLE_FIELD_TYPES,
  allowedSyncs,
  fieldMappingsPatch,
  isAllowedTarget,
  jiraClaimed,
  jiraPairings,
  withSync,
  type JiraPairingSync,
} from "@/lib/itsm-config/jira";
import type {
  JiraFieldMapping,
  JiraHarmonyField,
  ProviderConfig,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
  providerConfig: ProviderConfig;
}

const SYNC_LABELS: Record<JiraPairingSync, string> = {
  both: "Both ways",
  outbound: "Harmony → Jira",
  inbound: "Jira → Harmony",
  create: "At creation only",
};

const syncOptions = (field: JiraHarmonyField | null, target?: string) =>
  allowedSyncs(field, target).map((value) => ({
    value,
    // eslint-disable-next-line security/detect-object-injection -- value is a fixed sync union
    label: SYNC_LABELS[value],
  }));

/** Jira: Harmony fields paired with Jira fields, tenant-wide, and which way. */
export function JiraFieldMappingsSection({
  tenantId,
  credentialId,
  providerConfig,
}: Props) {
  const { update } = useDraft();
  // Mappings are tenant-wide, so every desk's fields can be paired - not
  // only the desks an export rule names.
  const { data: desks } = useDesks(tenantId);
  const deskIds = (desks ?? []).map((d) => d.id);
  const { fields, items, catalogLoading, label } = useHarmonyFieldLabels(
    tenantId,
    deskIds,
  );
  const siteFields = useJiraSiteFields(tenantId, credentialId);
  const [catalogItemId, setCatalogItemId] = useState("");
  const catalogItem = useSourceCatalogItem(tenantId, catalogItemId);
  const [newField, setNewField] = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [newSync, setNewSync] = useState<JiraPairingSync>("both");

  const explicit = providerConfig.field_mappings ?? [];
  const pairings = jiraPairings(providerConfig);
  const claimed = jiraClaimed(providerConfig);
  const jiraFields = siteFields.data?.fields ?? [];
  const jiraFieldName = (target: string) => {
    const field = jiraFields.find((f) => f.value === target);
    return field ? `${field.label} (${target})` : target;
  };

  const fieldOptions = [
    ...JIRA_BUILTIN_FIELDS.map(
      (key): JiraHarmonyField => ({ kind: "builtin", key }),
    ),
    ...fields.definitions.map(
      (d): JiraHarmonyField => ({ kind: "custom", key: d.id }),
    ),
    ...(catalogItemId
      ? (catalogItem.data?.form_fields ?? [])
          .filter((f) => f.label)
          .map(
            (f): JiraHarmonyField => ({
              kind: "catalog",
              key: String(f.label),
              catalog_item_id: catalogItemId,
            }),
          )
      : []),
  ]
    .filter((field) => !claimed.fields.has(fieldIdentity(field)))
    .map((field) => ({ value: encodeField(field), label: label(field) }));

  const chosen = newField ? decodeField(newField) : null;
  const targetOptions = jiraFields
    .filter((f) => !f.field_type || JIRA_WRITABLE_FIELD_TYPES.has(f.field_type))
    .filter((f) => isAllowedTarget(chosen, f.value))
    .filter((f) => !claimed.targets.has(f.value))
    .map((f) => ({
      value: f.value,
      label: `${f.label} (${f.field_type ?? f.value})`,
    }));

  const persist = (next: JiraFieldMapping[]) => {
    update(fieldMappingsPatch(next));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Field mappings</CardTitle>
        <CardDescription>
          Pair desk custom fields, catalog form fields and the title or
          description with Jira fields, and choose which way each syncs. Each
          Harmony field and each Jira field pairs once, across every project: a
          Jira field id names the same field site-wide, and a project whose
          screens lack it leaves it unset. Title and description reach Jira
          after creation only through a mapping here. Nothing comes back to the
          ticket unless its pairing says so.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-end">
          <JiraFieldImport tenantId={tenantId} credentialId={credentialId} />
        </div>
        {pairings.length === 0 ? (
          <p className="text-muted-foreground py-4 text-center text-sm">
            No field mappings. Jira syncs exactly as it does without them.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[260px]">Harmony field</TableHead>
                <TableHead>Jira field</TableHead>
                <TableHead className="w-[170px]">Direction</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pairings.map((p) => (
                <TableRow key={`${String(p.index)}|${p.target}`}>
                  <TableCell className="text-sm">
                    {label(p.harmony)}
                    {p.conflict && (
                      <Badge variant="destructive" className="ml-2 text-xs">
                        conflicts - remove to save
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {jiraFieldName(p.target)}
                  </TableCell>
                  <TableCell>
                    <MetadataSelect
                      aria-label={`Sync direction for ${label(p.harmony)}`}
                      options={syncOptions(p.harmony, p.target)}
                      value={p.sync}
                      onChange={(v) => {
                        persist(
                          explicit.map((m, i) =>
                            i === p.index
                              ? withSync(m, v as JiraPairingSync)
                              : m,
                          ),
                        );
                      }}
                      allowCustom={false}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        persist(explicit.filter((_, i) => i !== p.index));
                      }}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">
                        Remove {label(p.harmony)} mapping
                      </span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {siteFields.isError && (
          <p className="text-destructive text-sm">
            Could not read this Jira site&apos;s fields. Type a field id (e.g.
            customfield_10050) instead, or{" "}
            <button
              type="button"
              className="underline"
              onClick={() => {
                void siteFields.refetch();
              }}
            >
              try again
            </button>
            .
          </p>
        )}
        {catalogItem.isError && (
          <p className="text-destructive text-sm">
            Could not read this catalog item&apos;s form fields.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[200px]">
            <MetadataSelect
              aria-label="Catalog item"
              options={items.map((i) => ({ value: i.id, label: i.name }))}
              isLoading={catalogLoading}
              value={catalogItemId}
              onChange={(v) => {
                setCatalogItemId(v);
                setNewField("");
              }}
              placeholder="Catalog item (optional)"
              allowCustom={false}
            />
          </div>
          <div className="w-[240px]">
            <MetadataSelect
              aria-label="Harmony field"
              options={fieldOptions}
              isLoading={fields.isLoading || catalogItem.isLoading}
              value={newField}
              onChange={(v) => {
                const field = v ? decodeField(v) : null;
                setNewField(v);
                setNewTarget("");
                if (!allowedSyncs(field).includes(newSync)) {
                  setNewSync("outbound");
                }
              }}
              placeholder="Harmony field..."
              allowCustom={false}
            />
          </div>
          <div className="w-[240px]">
            <MetadataSelect
              aria-label="Jira field"
              options={targetOptions}
              isLoading={siteFields.isLoading}
              value={newTarget}
              onChange={(v) => {
                setNewTarget(v);
                if (!allowedSyncs(chosen, v).includes(newSync)) {
                  setNewSync("outbound");
                }
              }}
              placeholder="...to a Jira field"
              allowCustom={siteFields.isError}
              disabled={!newField}
            />
          </div>
          <div className="w-[170px]">
            <MetadataSelect
              aria-label="Sync direction"
              options={syncOptions(chosen, newTarget || undefined)}
              value={newSync}
              onChange={(v) => {
                setNewSync(v as JiraPairingSync);
              }}
              allowCustom={false}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={
              !chosen ||
              !newTarget ||
              !isAllowedTarget(chosen, newTarget) ||
              claimed.targets.has(newTarget)
            }
            onClick={() => {
              if (!chosen) return;
              persist([
                ...explicit,
                withSync({ harmony: chosen, target: newTarget }, newSync),
              ]);
              setNewField("");
              setNewTarget("");
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
